import { useEffect, useState } from "preact/hooks";
import Checkbox from "@/components/Checkbox";
import { Logic, QuestionAnswer } from "@/types/questions";
import QuestionContainer from "@/components/QuestionContainer";
import SlideFooter from "@/components/SlideFooter";

interface MultiSelectProps {
  onBack: (selectedAnswer: QuestionAnswer[] | null) => void;
  onNext: (selectedAnswers: QuestionAnswer[]) => void;
  questionText: string;
  questionSubText?: string;
  answers: QuestionAnswer[];
  logic?: Logic;
  defaultSelectedAnswers: QuestionAnswer[];
}

const MultiSelect = ({
  onBack,
  onNext,
  questionText,
  questionSubText,
  answers,
  logic,
  defaultSelectedAnswers,
}: MultiSelectProps) => {
  const [selectedAnswers, setSelectedAnswers] = useState<QuestionAnswer[]>(
    defaultSelectedAnswers
  );

  useEffect(() => {
    setSelectedAnswers(defaultSelectedAnswers);
  }, [questionText]);

  const handleSelection = (answer: QuestionAnswer) => {
    const isAlreadySelected = selectedAnswers.some((a) => a.id === answer.id);
    if (isAlreadySelected) {
      const newSelectedAnswers = selectedAnswers.filter(
        (a) => a.id !== answer.id
      );
      setSelectedAnswers(newSelectedAnswers);
      return;
    }

    if (logic?.max_selections) {
      if (selectedAnswers.length === logic?.max_selections) {
        return;
      }
    }

    if (logic?.disable_others_on_selection) {
      const handleDisableOthers = selectedAnswers.find(
        (a) => a.id === logic.disable_others_on_selection
      );

      if (
        answer.id === logic.disable_others_on_selection ||
        handleDisableOthers
      ) {
        setSelectedAnswers([answer]);
        return;
      }
    }

    setSelectedAnswers([...selectedAnswers, answer]);
  };

  const handleBack = () => {
    onBack(selectedAnswers);
  };

  const handleNext = () => {
    if (selectedAnswers) {
      onNext(selectedAnswers);
    }
  };

  return (
    <>
      <QuestionContainer legendText={questionText} subtext={questionSubText}>
        <div className="lc-grid lc-grid-cols-1 md:lc-grid-cols-2 lg:lc-grid-cols-3 lc-gap-6 lc-self-center lc-justify-center lc-w-full">
          {answers.map((answer) => (
            <Checkbox
              text={answer.text}
              onClick={() => handleSelection(answer)}
              active={selectedAnswers.includes(answer)}
            />
          ))}
        </div>
      </QuestionContainer>

      <SlideFooter
        onNext={handleNext}
        onBack={handleBack}
        disableContinue={selectedAnswers.length === 0}
      />
    </>
  );
};

export default MultiSelect;
